// src/pages/admin/SourceViewPage.js - תיקון לטעינת כל הנתונים
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase/firebaseConfig';
import adminAPI from '../../services/adminApi';
import { 
  ArrowLeft, Database, Loader, AlertCircle, Filter, 
  Eye, TrendingUp, TrendingDown, DollarSign, BarChart3,
  Search, RefreshCw
} from 'lucide-react';
import AdminNavbar from '../../components/admin/AdminNavbar';

const SourceViewPage = () => {
  const { sourceKey } = useParams();
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [fundsPerPage] = useState(20);

  const sourceInfo = {
    'gamal-net': { name: 'גמל-נט', color: 'blue', description: 'קופות גמל וקרנות השתלמות' },
    'pension-net': { name: 'פנסיה-נט', color: 'green', description: 'קרנות פנסיה' },
    'insurance-net': { name: 'ביטוח-נט', color: 'orange', description: 'פוליסות חיסכון' }
  };

  const source = sourceInfo[sourceKey] || { name: sourceKey, color: 'gray', description: 'מקור לא ידוע' };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadSourceData();
      }
    });

    return () => unsubscribe();
  }, [sourceKey, navigate]);

  const loadSourceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
     
      // טעינה ישירה מ-Storage המעובד
      const allFunds = await adminAPI.getAllFundsFromSource(sourceKey);
      
      setFunds(allFunds);
      
      
    } catch (error) {
      console.error(`❌ Error loading ${sourceKey} data:`, error);
      setError(`שגיאה בטעינת נתוני ${source.name}: ${error.message}`);
      setFunds([]);
    } finally {
      setLoading(false);
    }
  };

  const formatReturn = (value) => {
    if (value === null || value === undefined) return 'לא זמין';
    const num = parseFloat(value);
    if (isNaN(num)) return 'לא זמין';
    
    const color = num > 0 ? 'text-green-600' : num < 0 ? 'text-red-600' : 'text-gray-600';
    const icon = num > 0 ? TrendingUp : num < 0 ? TrendingDown : null;
    
    return (
      <div className={`flex items-center gap-1 ${color}`}>
        {icon && React.createElement(icon, { className: "w-3 h-3" })}
        <span>{num.toFixed(2)}%</span>
      </div>
    );
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return 'לא זמין';
    const num = parseFloat(value);
    if (isNaN(num)) return 'לא זמין';
    
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(2)}B ₪`;
    } else if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M ₪`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K ₪`;
    }
    return `${num.toLocaleString()} ₪`;
  };

  // סינון וחיפוש
  const filteredFunds = funds.filter(fund => {
    const matchesSearch = !searchQuery || 
      fund.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fund.id?.toString().includes(searchQuery) ||
      fund.company?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filter === 'all' || 
      fund.classification?.toLowerCase().includes(filter.toLowerCase());
    
    return matchesSearch && matchesFilter;
  });

  // מיון
  const sortedFunds = [...filteredFunds].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'ytd':
        aValue = parseFloat(a.returns?.ytd) || 0;
        bValue = parseFloat(b.returns?.ytd) || 0;
        break;
      case 'fees':
        aValue = parseFloat(a.fees?.management) || 0;
        bValue = parseFloat(b.fees?.management) || 0;
        break;
      case 'assets':
        aValue = parseFloat(a.financialData?.totalAssets || a.totalAssets) || 0;
        bValue = parseFloat(b.financialData?.totalAssets || b.totalAssets) || 0;
        break;
      default:
        return 0;
    }
    
    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // חלוקה לעמודים
  const indexOfLastFund = currentPage * fundsPerPage;
  const indexOfFirstFund = indexOfLastFund - fundsPerPage;
  const currentFunds = sortedFunds.slice(indexOfFirstFund, indexOfLastFund);
  const totalPages = Math.ceil(sortedFunds.length / fundsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">טוען קרנות מ{source.name}...</p>
            <p className="text-xs text-gray-500 mt-2">זה עלול לקחת מספר שניות...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* כותרת וניווט חזרה */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/data')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            חזרה לניהול נתונים
          </button>
          
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-${source.color}-500 flex items-center justify-center`}>
              <Database className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{source.name}</h1>
              <p className="text-gray-600">{source.description}</p>
            </div>
          </div>
          
          <button
            onClick={loadSourceData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            רענן
          </button>
        </div>

        {/* שגיאה */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-red-800 font-medium">שגיאה</h4>
              <p className="text-red-700 text-sm mt-1">{error}</p>
              <button 
                onClick={loadSourceData}
                className="text-red-600 hover:text-red-800 text-sm font-medium mt-2 underline"
              >
                נסה שוב
              </button>
            </div>
          </div>
        )}

        {/* סטטיסטיקות מהירות */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">{funds.length}</div>
            <div className="text-sm text-gray-600">סה״כ קרנות</div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-green-600">{filteredFunds.length}</div>
            <div className="text-sm text-gray-600">אחרי סינון</div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-purple-600">{totalPages}</div>
            <div className="text-sm text-gray-600">עמודים</div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-orange-600">{currentPage}</div>
            <div className="text-sm text-gray-600">עמוד נוכחי</div>
          </div>
        </div>

        {/* כלי בקרה */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* חיפוש */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">חיפוש</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="שם קרן, מזהה או חברה..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* סינון */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">סינון</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">כל הקרנות</option>
                <option value="גמל">קופות גמל</option>
                <option value="השתלמות">קרנות השתלמות</option>
                <option value="פנסיה">קרנות פנסיה</option>
                <option value="חיסכון">פוליסות חיסכון</option>
              </select>
            </div>

            {/* מיון */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">מיון לפי</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="name">שם הקרן</option>
                <option value="ytd">תשואה שנה עד היום</option>
                <option value="fees">דמי ניהול</option>
                <option value="assets">סך נכסים</option>
              </select>
            </div>

            {/* כיוון מיון */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">כיוון</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="asc">עולה</option>
                <option value="desc">יורד</option>
              </select>
            </div>
          </div>
        </div>

        {/* טבלת קרנות */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {currentFunds.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">אין קרנות להצגה</h3>
              <p className="text-gray-500">
                {funds.length === 0 
                  ? `לא ניתן לטעון נתונים מ${source.name}. בדוק את החיבור ל-API.`
                  : 'לא נמצאו קרנות המתאימות לקריטריונים שנבחרו'
                }
              </p>
            </div>
          ) : (
            <>
              {/* כותרות טבלה */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="col-span-3">פרטי קרן</div>
                  <div className="col-span-2">תשואות</div>
                  <div className="col-span-2">עמלות</div>
                  <div className="col-span-2">נכסים</div>
                  <div className="col-span-2">חברה</div>
                  <div className="col-span-1">פעולות</div>
                </div>
              </div>

              {/* שורות הנתונים */}
              <div className="divide-y divide-gray-200">
                {currentFunds.map((fund, index) => (
                  <div key={fund.id || index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* פרטי קרן */}
                      <div className="col-span-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <h4 className="font-medium text-gray-900 truncate">{fund.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {fund.id}
                              </span>
                              <span className="text-xs text-gray-500 truncate">
                                {fund.classification}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* תשואות */}
                      <div className="col-span-2">
                        <div className="space-y-1">
                          <div className="text-sm">
                            <span className="text-xs text-gray-500">YTD: </span>
                            {formatReturn(fund.returns?.ytd)}
                          </div>
                          <div className="text-sm">
                            <span className="text-xs text-gray-500">12M: </span>
                            {formatReturn(fund.returns?.year1)}
                          </div>
                        </div>
                      </div>

                      {/* עמלות */}
                      <div className="col-span-2">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-orange-600">
                            <DollarSign className="w-3 h-3" />
                            <span className="text-sm">
                              {fund.fees?.management ? `${fund.fees.management.toFixed(2)}%` : 'לא זמין'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">דמי ניהול</div>
                        </div>
                      </div>

                      {/* נכסים */}
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(fund.financialData?.totalAssets || fund.totalAssets)}
                        </div>
                        <div className="text-xs text-gray-500">סך נכסים</div>
                      </div>

                      {/* חברה */}
                      <div className="col-span-2">
                        <div className="text-sm text-gray-900 truncate">{fund.company}</div>
                      </div>

                      {/* פעולות */}
                      <div className="col-span-1">
                        <button
                          onClick={() => navigate(`/admin/fund/${fund.id}`)}
                          className="flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs rounded transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          צפה
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ניווט עמודים */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      מציג {indexOfFirstFund + 1}-{Math.min(indexOfLastFund, sortedFunds.length)} מתוך {sortedFunds.length} קרנות
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 bg-white border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50 transition-colors"
                      >
                        הקודם
                      </button>
                      
                      <span className="px-3 py-1 bg-blue-600 text-white rounded">
                        {currentPage}
                      </span>
                      
                      <span className="text-gray-500">מתוך {totalPages}</span>
                      
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 bg-white border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50 transition-colors"
                      >
                        הבא
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceViewPage;